import React, { useMemo, useState } from "react";
import { calculateBtu, BTU_TONS } from "../lib/btu";
import { useLang } from "../context/LanguageContext";

/**
 * Minimalist BTU calculator — single input (room area).
 * The underlying engine still supports all advanced parameters with
 * sensible defaults (no sun bias, 2 people, 2.7 m ceiling, etc.) so the
 * recommended BTU stays accurate for a "typical Jordanian room".
 *
 * Props
 *   compact       (bool) — slim styling for product-page side-panel
 *   onResult      (fn)   — fires whenever the recommendation changes
 *   ctaLabel/ctaTo       — optional CTA button under the result
 */
export default function BtuCalculator({
  compact = false,
  onResult,
  ctaLabel,
  ctaTo,
}) {
  const { t } = useLang();
  const [area, setArea] = useState(20);
  const result = useMemo(() => calculateBtu({ area }), [area]);

  React.useEffect(() => {
    if (onResult) onResult(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.recommended]);

  return (
    <div
      data-testid="btu-calculator"
      className={`bg-white border border-zinc-200 ${compact ? "p-4" : "p-6"}`}
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-electric">
            {t("calc.eyebrow")}
          </div>
          <h3 className="font-display font-black text-xl tracking-tight">
            {t("calc.title")}
          </h3>
        </div>
        {!compact && (
          <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">
            {t("calc.subtitle")}
          </div>
        )}
      </div>

      <label className="block">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
            {t("calc.area")}
          </span>
          <span className="font-display font-black text-3xl tabular-nums">
            {area}
            <span className="text-sm text-zinc-400 font-mono ml-1">m²</span>
          </span>
        </div>
        <input
          type="range"
          min={6}
          max={80}
          step={1}
          value={area}
          onChange={(e) => setArea(Number(e.target.value))}
          data-testid="calc-area-slider"
          className="w-full accent-electric"
        />
        <div className="flex justify-between text-[9px] font-mono text-zinc-400 mt-1 tabular-nums">
          <span>6 m²</span>
          <span>25</span>
          <span>50</span>
          <span>80 m²</span>
        </div>
      </label>

      <div
        data-testid="btu-result"
        className="mt-5 bg-ink text-white p-5 flex flex-col items-center text-center"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-electric mb-2">
          {t("calc.youNeed")}
        </div>
        <div className="font-display font-black text-4xl sm:text-5xl tabular-nums">
          {result.recommended.toLocaleString()}
          <span className="text-base text-zinc-400 font-mono ml-2">BTU</span>
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 mt-2">
          {BTU_TONS[result.recommended]}
        </div>
        {ctaTo && ctaLabel && (
          <a
            href={ctaTo}
            data-testid="btu-cta"
            className="inline-flex items-center justify-center h-10 px-5 mt-4 bg-electric text-ink font-mono uppercase tracking-[0.18em] text-[11px] hover:bg-white transition-colors"
          >
            {ctaLabel}
          </a>
        )}
      </div>

      <p className="text-[10px] text-zinc-400 mt-3">{t("calc.disclaimer")}</p>
    </div>
  );
}
